import type { MusicalToken, TimeSignature } from '../types/music';
import { normalizeTokenArray, tokenToSymbol, tokenToFigure, tokenBeats } from './tokens';
import { truncateTokensToBarCapacity } from './vex/vexFlowMapper';
import { isRestMusicalToken, restTokenBeats } from './scoreImport/restTokens';

/** Modificadores gráficos y de ligadura sobre una célula rítmica. */
export interface RhythmicCellModifiers {
  isTiedToNext?: boolean;
  hasDot?: boolean;
  /** Índice del token de origen en el compás parseado (trazabilidad MusicXML). */
  sourceTokenIndex?: number;
  /** Esta celda continúa una figura larga (blanca/redonda) del tiempo anterior. */
  isSpanContinuation?: boolean;
  /** Silencio de relleno sintético — no viene del XML. */
  isPadding?: boolean;
  /** Cuántos tiempos ocupa la figura (blanca = 2). */
  spanBeats?: number;
}

/**
 * Unidad atómica del Beat-Driven Engine — una figura o grupo reconocible
 * con duración en tiempos, sílaba Takadimi y tokens MusicXML subyacentes.
 */
export interface RhythmicCell {
  id: string;
  name: string;
  beatDuration: number;
  tokens: MusicalToken[];
  takadimi: string;
  isRest: boolean;
  modifiers?: RhythmicCellModifiers;
}

/** Definición de plantilla en el registro (sin índices de compás). */
export interface RhythmicCellDefinition {
  id: string;
  name: string;
  beatDuration: number;
  tokens: MusicalToken[];
  takadimi: string;
  isRest?: boolean;
}

export interface GroupTokensOptions {
  tiePairs?: Array<[number, number]>;
  dottedIndices?: number[];
}

export interface BarBeatStructure {
  timeSignature: TimeSignature;
  beatsPerBar: number;
  /** Una célula por tiempo del compás (suma beatDuration = 1 por slot). */
  beatCells: RhythmicCell[];
  /** Secuencia plana en orden de lectura. */
  cells: RhythmicCell[];
}

const SIGNATURE = (tokens: MusicalToken[]) => tokens.join('|');

function def(
  id: string,
  name: string,
  beatDuration: number,
  tokens: MusicalToken[],
  takadimi: string,
  isRest = false,
): RhythmicCellDefinition {
  return { id, name, beatDuration, tokens, takadimi, isRest };
}

/** Registro canónico — Takadimi como verdad pedagógica para agrupación. */
export const CELL_REGISTRY: RhythmicCellDefinition[] = [
  // Silencios — IDs canónicos SILENCIO_* con tokens R_* reales del parser
  def('SILENCIO_REDONDA', 'Silencio de redonda', 4, ['R_W'], 'Silencio', true),
  def('SILENCIO_BLANCA', 'Silencio de blanca', 2, ['R_H'], 'Silencio', true),
  def('SILENCIO_NEGRA', 'Silencio de negra', 1, ['R_Q'], 'Silencio', true),
  def('SILENCIO_CORCHEA', 'Silencio de corchea', 0.5, ['R_E'], 'Silencio', true),
  def('SILENCIO_SEMICORCHEA', 'Silencio de semicorchea', 0.25, ['R_S'], 'Silencio', true),

  // Figuras simples
  def('REDONDA', 'Redonda', 4, ['W'], 'Ta-a-a-a'),
  def('BLANCA', 'Blanca', 2, ['H'], 'Ta-a'),
  def('NEGRA', 'Negra', 1, ['Q'], 'Ta'),
  def('CORCHEA_SUELTA', 'Corchea suelta', 0.5, ['E'], 'di'),
  def('SEMICORCHEA', 'Semicorchea', 0.25, ['S'], 'ka'),

  // Compuestos — ordenados antes que simples repetidos en el matcher
  def('CUARTINA', 'Cuartina (son pololos)', 1, ['S', 'S', 'S', 'S'], 'Ta-ka-di-mi'),
  def('GALOPA', 'Galopa', 1, ['E', 'S', 'S'], 'Ta-ka-di'),
  def('GALOPA_INV', 'Galopa inversa', 1, ['S', 'S', 'E'], 'Ta-di-mi'),
  def('SALTILLO', 'Saltillo', 1, ['E', 'S'], 'Ta-mi'),
  def('SALTILLO_INV', 'Saltillo inverso', 1, ['S', 'E'], 'Ta-a-di'),
  def('TRESILLO_CORCHEAS', 'Tresillo de corcheas', 1, ['E', 'E', 'E'], 'Ta-ki-da'),
  def('TRESILLO_NEGRAS', 'Tresillo de negras', 2, ['Q', 'Q', 'Q'], 'Ta-ki-da'),
  def('PAR_CORCHEAS', 'Par de corcheas', 1, ['E', 'E'], 'Ta-di'),
  def('PAR_SEMICORCHEAS', 'Cuatro semicorcheas', 1, ['S', 'S', 'S', 'S'], 'Ta-ka-di-mi'),
];

/** Longitud descendente para greedy match. */
const REGISTRY_BY_SIGNATURE = [...CELL_REGISTRY].sort(
  (a, b) => b.tokens.length - a.tokens.length || b.beatDuration - a.beatDuration,
);

function cloneCell(
  definition: RhythmicCellDefinition,
  modifiers?: RhythmicCellModifiers,
): RhythmicCell {
  return {
    id: definition.id,
    name: definition.name,
    beatDuration: definition.beatDuration,
    tokens: [...definition.tokens],
    takadimi: definition.takadimi,
    isRest: definition.isRest === true,
    modifiers: modifiers ? { ...modifiers } : undefined,
  };
}

function parseBeatsPerBar(timeSignature: string): number {
  const m = String(timeSignature || '4/4').match(/^(\d+)\/(\d+)$/);
  if (!m) return 4;
  const numerator = Number(m[1]);
  const denominator = Number(m[2]);
  if (!Number.isFinite(numerator) || numerator < 1) return 4;
  // Compás simple: numerador = cantidad de tiempos (negra en 4/4).
  if (denominator === 4) return numerator;
  if (denominator === 8) return Math.max(1, Math.ceil(numerator / 2));
  if (denominator === 2) return numerator * 2;
  return numerator;
}

function tokenBeatValue(token: MusicalToken): number {
  if (isRestMusicalToken(token)) return restTokenBeats(token);
  return tokenBeats(token);
}

/** Agrupa tokens que caben en exactamente 1 tiempo — prioriza pares sobre tresillos. */
function groupBucketTokensToCell(bucket: MusicalToken[], sourceTokenIndex?: number): RhythmicCell {
  if (bucket.length === 1) {
    const definition = matchDefinitionAt(bucket, 0);
    if (definition) {
      return cloneCell(definition, { sourceTokenIndex: sourceTokenIndex ?? 0 });
    }
  }

  const signature = SIGNATURE(bucket);
  for (const entry of REGISTRY_BY_SIGNATURE) {
    if (SIGNATURE(entry.tokens) !== signature) continue;
    if (Math.abs(entry.beatDuration - 1) > 0.02) continue;
    if (entry.id === 'TRESILLO_NEGRAS') continue;
    return cloneCell(entry, { sourceTokenIndex });
  }

  return mergeBucketToCell(
    bucket.map((token, i) =>
      cloneCell(
        matchDefinitionAt([token], 0) ?? def('NEGRA', 'Negra', 1, [token], 'Ta'),
        { sourceTokenIndex: (sourceTokenIndex ?? 0) + i },
      ),
    ),
  );
}

/**
 * Construye exactamente un slot por tiempo del compás — agrupación beat-first.
 * Evita tresillos espurios (p. ej. Q|Q|Q en 3/4 → tres negras, no tresillo).
 */
function buildBeatCellsFromTokens(
  tokens: MusicalToken[],
  timeSignature: string,
  options: GroupTokensOptions = {},
): RhythmicCell[] {
  const beatsPerBar = parseBeatsPerBar(String(timeSignature));
  const beatCells: RhythmicCell[] = [];
  let idx = 0;

  while (beatCells.length < beatsPerBar && idx < tokens.length) {
    const token = tokens[idx]!;
    const dur = tokenBeatValue(token);

    if (dur > 1.001) {
      const definition =
        matchDefinitionAt(tokens, idx) ??
        (token === 'H'
          ? def('BLANCA', 'Blanca', 2, ['H'], 'Ta-a')
          : token === 'W'
            ? def('REDONDA', 'Redonda', 4, ['W'], 'Ta-a-a-a')
            : def('NEGRA', 'Negra', 1, [token], 'Ta'));
      const span = Math.min(Math.round(dur), beatsPerBar - beatCells.length);
      const base = cloneCell(definition, {
        sourceTokenIndex: idx,
        spanBeats: span,
      });
      beatCells.push(base);
      for (let s = 1; s < span; s += 1) {
        beatCells.push({
          ...cloneCell(definition, { sourceTokenIndex: idx, isSpanContinuation: true }),
          name: '·',
          takadimi: '·',
        });
      }
      idx += definition.tokens.length;
      continue;
    }

    const bucket: MusicalToken[] = [];
    let fill = 0;
    const startIdx = idx;
    while (idx < tokens.length && fill < 1 - 0.001) {
      const t = tokens[idx]!;
      const b = tokenBeatValue(t);
      if (bucket.length > 0 && fill + b > 1.001) break;
      bucket.push(t);
      fill += b;
      idx += 1;
    }
    if (!bucket.length) break;
    beatCells.push(groupBucketTokensToCell(bucket, startIdx));
  }

  while (beatCells.length < beatsPerBar) {
    beatCells.push(
      cloneCell(def('SILENCIO_NEGRA', 'Silencio de negra', 1, ['R_Q'], 'Silencio', true), {
        isPadding: true,
      }),
    );
  }

  const flatCells = beatCells.filter((cell) => !cell.modifiers?.isSpanContinuation);
  applyTieModifiers(flatCells, options.tiePairs);
  applyDotModifiers(flatCells, options.dottedIndices);
  return beatCells.slice(0, beatsPerBar);
}

/** Fusiona células parciales en un slot de 1 tiempo con Takadimi canónico. */
function mergeBucketToCell(bucket: RhythmicCell[]): RhythmicCell {
  if (bucket.length === 1) return bucket[0]!;

  const tokens = bucket.flatMap((cell) => cell.tokens);
  const duration = bucket.reduce((sum, cell) => sum + cell.beatDuration, 0);
  const signature = SIGNATURE(tokens);

  for (const entry of REGISTRY_BY_SIGNATURE) {
    if (SIGNATURE(entry.tokens) === signature && Math.abs(entry.beatDuration - duration) < 0.02) {
      return cloneCell(entry);
    }
  }

  if (
    bucket.length === 2 &&
    bucket.every((cell) => cell.tokens.length === 1 && cell.tokens[0] === 'E')
  ) {
    return cloneCell(def('PAR_CORCHEAS', 'Par de corcheas', 1, tokens, 'Ta-di'));
  }

  if (tokens.length === 4 && tokens.every((token) => token === 'S')) {
    return cloneCell(def('CUARTINA', 'Cuartina (son pololos)', 1, tokens, 'Ta-ka-di-mi'));
  }

  const uniqueTakadimi = [...new Set(bucket.map((cell) => cell.takadimi))];
  const takadimi =
    uniqueTakadimi.length === 1
      ? uniqueTakadimi[0]!
      : signature === 'E|E'
        ? 'Ta-di'
        : uniqueTakadimi.join(' · ');

  return {
    id: 'COMPOUND_BEAT',
    name: bucket.map((cell) => cell.name).join(' + '),
    beatDuration: duration,
    tokens,
    takadimi,
    isRest: bucket.every((cell) => cell.isRest),
    modifiers: bucket.find((cell) => cell.modifiers)?.modifiers,
  };
}

function matchDefinitionAt(tokens: MusicalToken[], start: number): RhythmicCellDefinition | null {
  for (const entry of REGISTRY_BY_SIGNATURE) {
    if (entry.id === 'TRESILLO_NEGRAS') continue;
    const slice = tokens.slice(start, start + entry.tokens.length);
    if (slice.length !== entry.tokens.length) continue;
    if (SIGNATURE(slice) === SIGNATURE(entry.tokens)) return entry;
  }
  const single = tokens[start];
  if (!single) return null;
  return REGISTRY_BY_SIGNATURE.find((entry) => entry.tokens.length === 1 && entry.tokens[0] === single) ?? null;
}

function tokenIndexToCellIndex(cells: RhythmicCell[], tokenIndex: number): number {
  let cursor = 0;
  for (let i = 0; i < cells.length; i += 1) {
    const span = cells[i]!.tokens.length;
    if (tokenIndex >= cursor && tokenIndex < cursor + span) return i;
    cursor += span;
  }
  return -1;
}

function applyTieModifiers(cells: RhythmicCell[], tiePairs: Array<[number, number]> = []): void {
  for (const [fromToken, toToken] of tiePairs) {
    if (!Number.isFinite(fromToken) || !Number.isFinite(toToken)) continue;
    const fromCellIdx = tokenIndexToCellIndex(cells, fromToken);
    if (fromCellIdx < 0) continue;
    const cell = cells[fromCellIdx];
    if (!cell) continue;
    cell.modifiers = {
      ...(cell.modifiers ?? {}),
      isTiedToNext: true,
      sourceTokenIndex: fromToken,
    };
    const toCellIdx = tokenIndexToCellIndex(cells, toToken);
    if (toCellIdx >= 0 && cells[toCellIdx]) {
      cells[toCellIdx].modifiers = {
        ...(cells[toCellIdx].modifiers ?? {}),
        sourceTokenIndex: toToken,
      };
    }
  }
}

function applyDotModifiers(cells: RhythmicCell[], dottedTokenIndices: number[] = []): void {
  for (const tokenIdx of dottedTokenIndices) {
    const cellIdx = tokenIndexToCellIndex(cells, tokenIdx);
    if (cellIdx < 0) continue;
    const cell = cells[cellIdx];
    if (!cell) continue;
    cell.modifiers = { ...(cell.modifiers ?? {}), hasDot: true, sourceTokenIndex: tokenIdx };
    cell.beatDuration = Math.min(cell.beatDuration * 1.5, 4);
    cell.name = `${cell.name} (puntillo)`;
  }
}

/**
 * Agrupa tokens crudos del parser MusicXML en células rítmicas reconocibles.
 */
export function groupTokensIntoCells(
  barTokens: MusicalToken[] | unknown[],
  timeSignature: TimeSignature | string = '4/4',
  options: GroupTokensOptions = {},
): RhythmicCell[] {
  const tokens = normalizeTokenArray(barTokens);
  if (!tokens.length) {
    return [cloneCell(def('NEGRA', 'Negra', 1, ['Q'], 'Ta'))];
  }

  const beatCells = buildBeatCellsFromTokens(tokens, String(timeSignature), options);
  return beatCells.filter((cell) => !cell.modifiers?.isSpanContinuation && !cell.modifiers?.isPadding);
}

/**
 * Empaqueta células en exactamente un slot por tiempo del compás.
 * Combina células parciales (< 1 tiempo) dentro del mismo slot.
 */
export function packCellsIntoBeats(
  cells: RhythmicCell[],
  timeSignature: TimeSignature | string = '4/4',
): RhythmicCell[] {
  const beatsPerBar = parseBeatsPerBar(String(timeSignature));
  if (
    cells.length === beatsPerBar &&
    cells.every((cell) => Math.abs(cell.beatDuration - 1) < 0.02 || cell.modifiers?.isSpanContinuation)
  ) {
    return cells.slice(0, beatsPerBar);
  }

  const beatCells: RhythmicCell[] = [];
  let cursor = 0;
  let fill = 0;
  let bucket: RhythmicCell[] = [];

  const flushBucket = () => {
    if (!bucket.length) return;
    beatCells.push(mergeBucketToCell(bucket));
    bucket = [];
    fill = 0;
  };

  while (cursor < cells.length && beatCells.length < beatsPerBar) {
    const cell = cells[cursor]!;
    cursor += 1;

    if (Math.abs(cell.beatDuration - 1) < 0.001 && !bucket.length) {
      beatCells.push(cell);
      continue;
    }

    bucket.push(cell);
    fill += cell.beatDuration;

    if (fill >= 1 - 0.001) {
      flushBucket();
    }
  }

  flushBucket();

  while (beatCells.length < beatsPerBar) {
    beatCells.push(
      cloneCell(def('SILENCIO_NEGRA', 'Silencio de negra', 1, ['R_Q'], 'Silencio', true), {
        isPadding: true,
      }),
    );
  }

  return beatCells.slice(0, beatsPerBar);
}

/** Estructura completa de compás para payloads UI-ready. */
export function buildBarBeatStructure(
  barTokens: MusicalToken[] | unknown[],
  timeSignature: TimeSignature | string = '4/4',
  options: GroupTokensOptions = {},
): BarBeatStructure {
  const ts = (['2/4', '3/4', '4/4'].includes(String(timeSignature)) ? timeSignature : '4/4') as TimeSignature;
  const tokens = truncateTokensToBarCapacity(normalizeTokenArray(barTokens), ts);
  const beatCells = buildBeatCellsFromTokens(tokens, ts, options);
  const cells = beatCells.filter((cell) => !cell.modifiers?.isSpanContinuation && !cell.modifiers?.isPadding);
  return {
    timeSignature: ts,
    beatsPerBar: beatCells.length,
    beatCells,
    cells,
  };
}

/** Convierte células al formato legacy `rhythmCells` del payload (compat UI existente). */
export function rhythmicCellsToLegacyPayload(cells: RhythmicCell[]) {
  return cells.map((cell) => ({
    token: cell.tokens[0] ?? 'Q',
    tokens: cell.tokens,
    syllable: cell.takadimi,
    figure: tokenToFigure(cell.tokens[0] ?? 'Q'),
    symbol: tokenToSymbol(cell.tokens[0] ?? 'Q'),
    name: cell.name,
    beatDuration: cell.beatDuration,
    isRest: cell.isRest,
    modifiers: cell.modifiers,
  }));
}

/** Tokens planos derivados de células (wire format retrocompatible). */
export function flattenCellTokens(cells: RhythmicCell[]): MusicalToken[] {
  return cells.flatMap((cell) => cell.tokens);
}

export function lookupCellById(id: string): RhythmicCellDefinition | undefined {
  if (id === 'DOBLE_CORCHEA') {
    return CELL_REGISTRY.find((entry) => entry.id === 'PAR_CORCHEAS');
  }
  if (id === 'CORCHEA') {
    return CELL_REGISTRY.find((entry) => entry.id === 'CORCHEA_SUELTA');
  }
  return CELL_REGISTRY.find((entry) => entry.id === id);
}

export function inferCellFromTakadimi(syllable: string): RhythmicCellDefinition | undefined {
  const normalized = String(syllable || '').trim().toLowerCase();
  return CELL_REGISTRY.find((entry) => entry.takadimi.toLowerCase() === normalized);
}
